import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, UserCheck } from "lucide-react";

const rupee = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const REASON_LABEL = {
  same_name: "Same name (case/dash/&-insensitive)",
  similar_name: "Similar name",
  similar_code_same_category: "Similar code in the same category",
};

/**
 * Head Review — Super Admin approves incoming head codes with category,
 * name, and optional merge-into an existing active project head.
 *
 * Props:
 *   projectId, projectCode
 *   open, onOpenChange(bool)
 *   pendingHeads: [{code, name, lines, amount, suggested_category, suggested_name}]
 *   onApproved(): called after successful POST to /v2/projects/{pid}/heads/review
 */
export default function HeadReviewDialog({ projectId, projectCode, open, onOpenChange, pendingHeads, onApproved }) {
  const [categories, setCategories] = useState([]);
  const [activeHeads, setActiveHeads] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [cats, existing] = await Promise.all([
          api.get("/v2/budget-categories").catch(() =>
            api.get("/masters/budget-categories").catch(() =>
              api.get("/v2/masters/categories").catch(() => ({ data: [] })))),
          api.get(`/v2/projects/${projectId}/heads`).catch(() => ({ data: [] })),
        ]);
        const catList = (cats?.data || []).map((c) => ({
          code: c.code, name: c.name, description: c.description || "",
        })) || [];
        // Fallback list — matches the 8 seeded categories.
        setCategories(catList.length ? catList : [
          { code: "PC", name: "Programme Costs", description: "Direct programme delivery" },
          { code: "CAPEX", name: "Capital Expense", description: "One-time asset purchase" },
          { code: "HR", name: "Personnel", description: "Salaries & benefits" },
          { code: "TR", name: "Travel & Transportation", description: "Travel, per-diem, fuel" },
          { code: "OPS", name: "Operations & Setup", description: "Operating & setup costs" },
          { code: "AV", name: "Audit & Visibility", description: "Audit fees & branding" },
          { code: "ADM", name: "Administrative", description: "Overheads (capped at 5%)" },
          { code: "CONT", name: "Contingency", description: "Reserve (capped at 2%)" },
        ]);
        setActiveHeads((existing.data || []).filter((h) => h.status === "active"));
      } catch (e) {
        toast.error(`Failed to load categories: ${e.message}`);
      }
    })();
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    setRows((pendingHeads || []).map((h, i) => ({
      code: h.code,
      name: h.name || h.suggested_name || "",
      category_code: h.suggested_category || "",
      sort_order: 100 + i,
      merge_into: "",
      lines: h.lines,
      amount: h.amount,
      similar_to: h.similar_to || null,
      // "Approve as new head anyway?" — pre-ticked only when there's no
      // similarity warning to acknowledge.
      confirmed_similar: !h.similar_to,
    })));
  }, [open, pendingHeads]);

  const update = (idx, key, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const canApprove = useMemo(
    () => rows.length > 0 && rows.every((r) => {
      if (r.merge_into) return true;
      if (!(r.name.trim() && r.category_code)) return false;
      if (r.similar_to && !r.confirmed_similar) return false;
      return true;
    }),
    [rows]
  );

  const submit = async () => {
    setSaving(true);
    try {
      // Split: heads to create/keep, and heads to merge into an existing head.
      const toApprove = rows
        .filter((r) => !r.merge_into)
        .map((r) => ({
          code: r.code.toUpperCase(),
          name: r.name.trim(),
          category_code: r.category_code,
          sort_order: r.sort_order,
          confirmed_similar: !!r.confirmed_similar,
        }));
      if (toApprove.length) {
        await api.post(`/v2/projects/${projectId}/heads/review`, {
          project_id: projectId,
          heads: toApprove,
        });
      }
      // Merge acts as an alias only for this iteration — surface it as a toast so the user retries the upload with the merged code.
      const merged = rows.filter((r) => r.merge_into);
      if (merged.length) {
        toast.message(`Merge planned for ${merged.map((r) => `${r.code}\u2192${r.merge_into}`).join(", ")} — please edit the file to use the target code and re-upload.`);
      }
      toast.success(`Head review approved (${toApprove.length} head${toApprove.length === 1 ? "" : "s"}).`);
      onApproved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || `Approve failed: ${e.message}`);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="head-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-4 h-4" /> Head Review — {projectCode}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 -mt-2 mb-2">
          Set the category and name for each new head, or merge it into an existing head. Approve to unlock import.
        </div>
        <div className="border rounded bg-slate-50/60 px-3 py-2 mb-2" data-testid="hr-category-legend">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
            Categories legend
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            {categories.map((c) => (
              <span key={c.code} className="inline-flex items-baseline gap-1" data-testid={`hr-legend-${c.code}`}>
                <span className="font-mono font-semibold text-slate-800">{c.code}</span>
                <span className="text-slate-600">· {c.name}</span>
                {c.description ? (
                  <span className="text-slate-400 italic">— {c.description}</span>
                ) : null}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-auto border rounded max-h-[52vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="border-b">
                <th className="text-left px-2 py-1.5 w-20">Code</th>
                <th className="text-left px-2 py-1.5">Name</th>
                <th className="text-left px-2 py-1.5 w-40">Category</th>
                <th className="text-left px-2 py-1.5 w-40">Or merge into</th>
                <th className="text-right px-2 py-1.5 w-16">Lines</th>
                <th className="text-right px-2 py-1.5 w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <React.Fragment key={r.code}>
                  <tr className="border-b" data-testid={`hr-row-${r.code}`}>
                    <td className="px-2 py-1.5 font-mono">{r.code}</td>
                    <td className="px-2 py-1.5">
                      <Input
                        className="h-7 text-xs"
                        value={r.name}
                        onChange={(e) => update(i, "name", e.target.value)}
                        disabled={!!r.merge_into}
                        data-testid={`hr-name-${r.code}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Select value={r.category_code} onValueChange={(v) => update(i, "category_code", v)} disabled={!!r.merge_into}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`hr-cat-${r.code}`}>
                          <SelectValue placeholder="Pick..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.code} · {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5">
                      <Select value={r.merge_into || "__none__"} onValueChange={(v) => update(i, "merge_into", v === "__none__" ? "" : v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`hr-merge-${r.code}`}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— (keep as new)</SelectItem>
                          {activeHeads.map((h) => (
                            <SelectItem key={h.code} value={h.code}>
                              {h.code} · {h.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.lines}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{rupee(r.amount)}</td>
                  </tr>
                  {r.similar_to && !r.merge_into && (
                    <tr className="border-b bg-amber-50" data-testid={`hr-similar-${r.code}`}>
                      <td colSpan={6} className="px-3 py-2">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="text-xs text-amber-900">
                              Looks similar to{" "}
                              <span className="font-mono font-semibold">{r.similar_to.code}</span>
                              {" — "}
                              <span className="italic">"{r.similar_to.name}"</span>
                              {r.similar_to.category_code ? (
                                <span className="text-amber-700"> · category {r.similar_to.category_code}</span>
                              ) : null}
                              {" ("}
                              {REASON_LABEL[r.similar_to.match_reason] || r.similar_to.match_reason}
                              {r.similar_to.score ? ` · match ${Math.round(r.similar_to.score * 100)}%` : ""}
                              {r.similar_to.source === "head_library" ? " · from library" : ""}
                              {")"}
                            </div>
                            <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                              <Checkbox
                                checked={!!r.confirmed_similar}
                                onCheckedChange={(v) => update(i, "confirmed_similar", !!v)}
                                data-testid={`hr-confirm-similar-${r.code}`}
                              />
                              Approve as a new head anyway?{" "}
                              <span className="text-amber-700">(or use the Merge column to fold this into {r.similar_to.code})</span>
                            </label>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="p-4 text-center text-slate-500" colSpan={6}>No pending heads.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canApprove || saving} data-testid="hr-approve-btn">
            {saving ? "Approving..." : "Approve heads"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
